import { cloneDeep, concat, remove } from "lodash-es";
import { type ReactElement, forwardRef, useEffect } from "react";
import { connect } from "react-redux";
import type { InjectedFormProps } from "redux-form";

import { HStack, Spacer, toTimeAgo } from "widgets";

import style from "../styles/forms.module.scss";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { splitProjectsAndFolders } from "@/modules/projects/utils/getProjectsByUserGroups.js";
import { simpleDropdownFieldValidation } from "../../../app-utils/form.js";
import type { Project } from "../../../graphql.js";
import { getProjectLabel } from "../../../redux/modules/config.js";
import { loadProjectList } from "../../../redux/modules/project-list.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../redux-form/ReactSelectField.js";

// --- Redux ---
function mapStateToProps(state: IAppState) {
  return {
    projectList: state.projectList,
    lastSaveTarget: state.auth.session?.params?.lastSaveTarget,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch) {
  return {
    loadProjectList: (sampleTypeId: number, includeStagedProjects: boolean) =>
      dispatch(loadProjectList(true, sampleTypeId, includeStagedProjects)), // throwError for onError cb
  };
}

// --- Component ---
type ProjectsFilter = (projects: Project[]) => Project[];

// Maybe filter the list of projects, if filter is defined
// Also put the private projects at the top of the list, whatever the filter does
function applyFilter(filter: ProjectsFilter | null | undefined, projectList: Project[]): Project[] {
  let projects = cloneDeep(projectList);

  try {
    if (typeof filter === "function") {
      projects = filter(projects);
    }
  } catch {
    /* don't apply filter if filter() fails */
  }

  // Do not use .sort(p => (p.isPrivateWorkspace ? -1 : 1))
  // as sort() seems to reverse the ordering done in the backend (only firefox?)
  const privates = remove(projects, (p) => p.isPrivateWorkspace); // modify options
  projects = concat(privates, projects);

  return projects;
}

interface IProjectsSelectProps {
  name: string;
  onInitToLastTarget?: InjectedFormProps["change"]; // Redux-form change() api ; do not set if you don't want the prefill
  filter?: ProjectsFilter;
  title?: string | ReactElement;
  placeholder?: string;
  tooltip?: string;
  horizontalLayout?: boolean;
  forceWarnState?: string | ReactElement;
  onChange?: (project: Project) => void;
  onError?: (error: Error) => void;
  sampleTypeId?: number; // if specified, filter the projects on the ones associated with the same user group like the sample type
  includeStagedProjects?: boolean; // meant for accessing resources, not when the project selection is meant as a target for creation of resources
  info?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
}

function ProjectsSelect(
  props: IProjectsSelectProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> & { forwardedRef: any },
) {
  useEffect(() => {
    // load Projects
    void props
      .loadProjectList(props.sampleTypeId, props.includeStagedProjects)
      .then((projectList) => {
        // Set the default project to the last selected one, or the first in the list
        const projects = applyFilter(props.filter, projectList.projects ?? []);

        if (typeof props.onInitToLastTarget === "function") {
          // The last used project is always selected
          const defaultSelectedProject =
            projects.find((project) => project.id === props.lastSaveTarget) || (projects.length > 0 && projects[0]);

          props.onInitToLastTarget(props.name, defaultSelectedProject);

          // trigger on Change as one project is selected
          onChange(defaultSelectedProject);
        }
      })
      .catch(props.onError);
  }, []);

  function onChange(project: Project) {
    // saving the currently selected project is done on the server-side.
    if (typeof props.onChange === "function") {
      props.onChange(project);
    }
  }

  let options = applyFilter(props.filter, props.projectList.projects ?? []);
  if (props.includeStagedProjects) {
    options = splitProjectsAndFolders(options);
  }

  return (
    <ReactSelectReduxFormField
      name={props.name}
      horizontalLayout={props.horizontalLayout || false}
      label={props.title || getProjectLabel(true, false)}
      tooltip={!props.isDisabled && props.tooltip}
      placeholder={props.placeholder || `Please select a ${getProjectLabel(false, false)}...`}
      component={ReactSelectField}
      isClearable={props.isClearable ?? false}
      isSearchable={true}
      options={options}
      isLoading={props.projectList.isLoading}
      validate={simpleDropdownFieldValidation}
      getOptionValue={(project: Project) => project.id}
      getOptionLabel={(project: Project) => project.name}
      formatOptionLabel={(project: Project) => (
        <HStack>
          <ProjectColoredLink project={project} noLink />
          <Spacer />
          <div className={style.timeago}>{toTimeAgo(project.lastModified)}</div>
        </HStack>
      )}
      ref={props.forwardedRef}
      handleChange={onChange}
      forceWarnState={props.forceWarnState}
      info={props.info}
      isDisabled={props.isDisabled}
    />
  );
}

const ConnectedProjectsSelect = connect(mapStateToProps, mapDispatchToProps)(ProjectsSelect);

export default forwardRef(function ForwardedProjectsSelect(props: IProjectsSelectProps, ref) {
  return <ConnectedProjectsSelect {...props} forwardedRef={ref} />;
});
